/**
 * Connector manifest — the declarative source of truth for what a connector
 * can DO, keyed by its ``capability``.
 *
 * This is the spine of the unified connector UX (Jul 2026 refactor). Instead
 * of three uncoordinated fork points —
 *   1. a hard-coded ``isFilesystem`` branch inside ``ConnectedAccountRow``,
 *   2. a DB/ERP-only ``extraMenuItems`` injection in ``$capability.tsx``,
 *   3. a ``MANAGE_HANDLERS`` map whose fallback fired a "coming soon" toast,
 * — the surfaces read this manifest and compose the same experience. Adding a
 * connector becomes a data entry, not new UI.
 *
 * PROGRESSIVE DISCLOSURE (SOTA 2026): a connector only advertises the actions
 * it actually supports. ``manage: null`` means there is nothing to manage yet,
 * so the row shows NO primary "Gestisci" button (killing the 12 dead-end
 * buttons that only toasted "in arrivo").
 *
 * The keys are the runtime ``ConnectionSummary.capability`` values — i.e. the
 * same keys the legacy ``MANAGE_HANDLERS`` dispatched on.
 */

/** What the primary "manage" affordance opens for a connector. */
export type ManageSurface =
  | "syncFilters" // email — which labels/threads to ingest
  | "syncChannels" // messaging — which channels
  | "syncFolders" // drive — which folders
  | "schema" // spreadsheet / DB — tables & columns
  | "editCredentials" // DB / ERP — host/port/credentials + test
  | "syncNow"; // filesystem — re-scan the folder now

/** Tabs a connector's detail drawer can show (rendered only if present). */
export type ConnectorTabId =
  | "overview"
  | "schema"
  | "context"
  | "examples"
  | "sync"
  | "credentials";

export interface ConnectorManifestEntry {
  /** Primary manage surface, or ``null`` when there's nothing to manage yet. */
  readonly manage: ManageSurface | null;
  /** Whether the connector can re-authenticate (OAuth/token). Filesystem can't. */
  readonly reconnectable: boolean;
  /** Copy for the destructive action: an account vs a local folder. */
  readonly removeKind: "account" | "folder";
  /**
   * Ordered tabs shown in the unified detail drawer. Empty → the connector
   * has no rich detail surface (only the row-level reconnect/disconnect).
   */
  readonly tabs: readonly ConnectorTabId[];
}

const DEFAULT_ENTRY: ConnectorManifestEntry = {
  manage: null,
  reconnectable: true,
  removeKind: "account",
  tabs: [],
};

/**
 * The manifest. Only the 7 capabilities with a real management surface today
 * are listed; everything else falls through to ``DEFAULT_ENTRY`` (no manage
 * surface → no primary button). F5 will graduate the "NUOVO" capabilities
 * (CRM, calendar, PEC, accounting, …) by giving them a ``manage`` surface.
 */
const DB_TABS = ["schema", "context", "examples"] as const;

const MANIFEST: Readonly<Record<string, ConnectorManifestEntry>> = {
  email: { manage: "syncFilters", reconnectable: true, removeKind: "account", tabs: [] },
  // Aruba PEC + InfoCert Legalmail — both plain IMAP mailboxes → the SAME
  // date/label filter surface as email (route to /email-management/* with
  // provider="aruba"/"infocert"; the BE compiles filters to IMAP SEARCH and
  // resolves the provider's IMAPS host). The two most-used PEC providers in IT.
  aruba_pec: { manage: "syncFilters", reconnectable: true, removeKind: "account", tabs: [] },
  infocert_pec: { manage: "syncFilters", reconnectable: true, removeKind: "account", tabs: [] },
  messaging: { manage: "syncChannels", reconnectable: true, removeKind: "account", tabs: [] },
  drive: { manage: "syncFolders", reconnectable: true, removeKind: "account", tabs: [] },
  spreadsheet: { manage: "schema", reconnectable: false, removeKind: "account", tabs: ["schema"] },
  filesystem: { manage: "syncNow", reconnectable: false, removeKind: "folder", tabs: [] },
  database: {
    manage: "editCredentials",
    reconnectable: false,
    removeKind: "account",
    tabs: DB_TABS,
  },
  erp: { manage: "editCredentials", reconnectable: false, removeKind: "account", tabs: DB_TABS },
};

/** Resolve the manifest for a capability (never throws — unknown → default). */
export function getConnectorManifest(capability: string): ConnectorManifestEntry {
  return MANIFEST[capability] ?? DEFAULT_ENTRY;
}

/** True when the connector exposes a primary "manage" surface. */
export function hasManageSurface(capability: string): boolean {
  return getConnectorManifest(capability).manage !== null;
}

/** Ordered detail-drawer tabs for a capability (empty when it has none). */
export function getConnectorTabs(capability: string): readonly ConnectorTabId[] {
  return getConnectorManifest(capability).tabs;
}

/** True when the connector has a rich detail drawer (≥1 tab). */
export function hasDetailDrawer(capability: string): boolean {
  return getConnectorTabs(capability).length > 0;
}
